import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  MdArrowBack,
  MdDashboard,
  MdBugReport,
  MdTimeline,
  MdDescription,
  MdCalendarToday,
  MdSpeed,
  MdAssignment,
  MdSecurity,
  MdAccessTime,
  MdTrendingUp,
  MdHistory,
  MdChevronRight,
  MdPerson,
  MdRefresh,
  MdWebAsset,
  MdKeyboardArrowRight,
  MdInfo,
  MdOpenInNew,
  MdKeyboardArrowLeft,
  MdLocationOn,
  MdCategory,
  MdCheckCircle,
  MdArrowForward,
  MdShield
} from "react-icons/md";
import { FiZap, FiLayers, FiSearch } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import axios from "../../utils/api/axios";
import { CTBProgram, parseProgramDetails } from "../../utils/api/endpoints/programs/parsePrograms";
import { isProgramDraft } from "../../utils/programs/statusUtils";
import ProgramCard from "../../components/programs/cards/ProgramCard";
import toast from "react-hot-toast";
import moment from "moment";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";

// Role constants (matches backend UserRole enum)
import { UserRole } from "../../utils/api/endpoints/user/credentials";
const ROLE_BUSINESS             = UserRole.BUSINESS;
const ROLE_BUSINESS_ADMINISTRATOR = UserRole.BUSINESS_ADMINISTRATOR;

// ─── helpers ────────────────────────────────────────────────────────────────
const safeArray = (val: any): any[] => (Array.isArray(val) ? val : []);

const roleLabel: Record<number, string> = {
  1: "Researcher",
  2: "Business Owner",
  3: "System Admin",
  4: "Admin Manager",
  5: "Account Owner",
  6: "Security Engineer",
  7: "Program Manager",
  8: "Organization Admin",
  9: "Sub-Admin",
  10: "Developer",
  11: "Business Manager"
};

interface QuotaBlock {
  quota: number;
  used: number;
  remaining: number;
  duration: number;
}

// ─── quota card with progress bar ───────────────────────────────────────────
const QuotaCard: React.FC<{ label: string; icon: React.ReactNode; accent: string; block: QuotaBlock }> = ({
  label, icon, accent, block
}) => {
  const pct = block.quota > 0 ? Math.min((block.used / block.quota) * 100, 100) : 0;
  
  // Dynamic styles based on usage
  const isHigh = pct >= 70 && pct < 90;
  const isCritical = pct >= 90;

  const barColor = isCritical ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.4)]" : isHigh ? "bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.4)]" : "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.4)]";
  const badgeColor = isCritical ? "bg-red-50 text-red-600 border border-red-100" : isHigh ? "bg-amber-50 text-amber-600 border border-amber-100" : "bg-emerald-50 text-emerald-700 border border-emerald-100";
  const badgeLabel = isCritical ? "Nearly Full" : isHigh ? "High Usage" : "Healthy";

  return (
    <div className="relative group bg-white/70 backdrop-blur-md rounded-3xl border border-white/50 shadow-xl shadow-gray-200/40 p-7 transition-all duration-300 hover:shadow-2xl hover:shadow-gray-300/50 hover:-translate-y-1 overflow-hidden">
      {/* Background Accent Glow */}
      <div className={`absolute -right-8 -top-8 w-32 h-32 rounded-full opacity-[0.03] group-hover:opacity-[0.06] transition-opacity ${accent}`} />
      
      <div className="relative flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-2xl ${accent} flex items-center justify-center shadow-inner`}>
            <div className="text-xl drop-shadow-sm">{icon}</div>
          </div>
          <div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] leading-none mb-1.5">{label}</p>
            <p className="text-xs font-medium text-gray-500">{block.duration} days per run</p>
          </div>
        </div>
      </div>
      
      <div className="relative grid grid-cols-3 gap-2 mb-8">
        {[
          { label: "Total", val: block.quota, color: "text-gray-900" },
          { label: "Used", val: block.used, color: "text-gray-900" },
          { label: "Left", val: block.remaining, color: block.remaining === 0 ? "text-red-500" : "text-emerald-600" }
        ].map((item, idx) => (
          <div key={item.label} className={`flex flex-col items-center ${idx < 2 ? "border-r border-gray-100/80" : ""}`}>
            <p className={`text-2xl font-black tracking-tight ${item.color}`}>{item.val}</p>
            <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-1">{item.label}</p>
          </div>
        ))}
      </div>
      
      <div className="relative pt-2">
        <div className="flex justify-between items-end mb-2.5">
          <span className="text-xs font-bold text-gray-500 italic">{pct.toFixed(0)}% Utilized</span>
          <span className="text-lg font-black text-gray-900 leading-none">{block.remaining} <span className="text-[10px] text-gray-400 uppercase tracking-tighter align-middle font-bold ml-1">Left</span></span>
        </div>
        <div className="w-full bg-gray-100/80 rounded-full h-3 p-0.5 border border-gray-200/50">
          <div 
            className={`${barColor} h-full rounded-full transition-all duration-1000 ease-out`} 
            style={{ width: `${pct}%` }} 
          />
        </div>
      </div>
    </div>
  );
};

// ─── empty state ─────────────────────────────────────────────────────────────
const EmptyState: React.FC<{ label: string; icon?: React.ReactNode }> = ({ label, icon }) => (
  <motion.div 
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    className="col-span-full flex flex-col items-center justify-center py-20 text-gray-400 bg-gray-50/50 rounded-2xl border border-dashed border-gray-200"
  >
    <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center text-2xl mb-4 opacity-60">
      {icon || <MdDescription />}
    </div>
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">{label}</p>
  </motion.div>
);

// ─── severity badge ─────────────────────────────────────────────────────────
const SeverityBadge: React.FC<{ sev: string }> = ({ sev }) => {
  const map: Record<string, { bg: string, text: string, dot: string }> = {
    CRITICAL: { bg: "bg-red-50",     text: "text-red-700",    dot: "bg-red-500" },
    HIGH:     { bg: "bg-orange-50",  text: "text-orange-700", dot: "bg-orange-500" },
    MEDIUM:   { bg: "bg-amber-50",   text: "text-amber-700",  dot: "bg-amber-500" },
    LOW:      { bg: "bg-blue-50",    text: "text-blue-700",   dot: "bg-blue-500" }
  };
  const config = map[sev] || { bg: "bg-gray-50", text: "text-gray-600", dot: "bg-gray-400" };
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border border-white/50 ${config.bg} ${config.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {sev}
    </span>
  );
};

// ─── Activity log row ─────────────────────────────────────────────────────
const LogRow: React.FC<{ log: any }> = ({ log }) => (
  <div className="group flex items-start gap-5 px-6 py-4 border-b border-gray-50 hover:bg-blue-50/20 transition-all duration-300">
    <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:scale-105 transition-transform shadow-inner">
      <MdHistory className="text-blue-500 text-base" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-0.5">
        <span className="text-[9px] font-black uppercase tracking-widest text-blue-600/50">{log.module || "System"}</span>
        <span className="w-1 h-1 rounded-full bg-gray-200" />
        <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 tabular-nums">
          {moment(log.created_at).format("h:mm:ss A")}
        </span>
      </div>
      <p className="text-[13px] text-gray-900 font-black tracking-tight leading-tight uppercase group-hover:text-blue-700 transition-colors">{log.action}</p>
      {log.user && (
        <div className="flex items-center gap-1.5 mt-1.5">
          <div className="w-3.5 h-3.5 rounded-full bg-gray-100 flex items-center justify-center text-[7px] text-gray-400">
            <MdPerson />
          </div>
          <span className="text-[9px] font-bold text-gray-400 opacity-80">{log.user.username || log.user.email.split('@')[0]}</span>
        </div>
      )}
    </div>
    <div className="text-right flex flex-col items-end gap-0.5">
      <span className="text-[9px] font-black uppercase tracking-widest text-gray-400">{moment(log.created_at).format("MMM D")}</span>
      <span className="text-[8px] font-bold text-gray-300 whitespace-nowrap">{moment(log.created_at).fromNow()}</span>
    </div>
  </div>
);

// ─── User card for manager view ──────────────────────────────────────────────
const UserCard: React.FC<{ user: any; onClick: () => void; logCount: number }> = ({ user, onClick, logCount }) => (
  <button
    onClick={onClick}
    className="w-full text-left bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 hover:shadow-md hover:border-blue-100 transition-all duration-300 group"
  >
    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center flex-shrink-0 shadow-inner group-hover:scale-105 transition-transform">
      <MdPerson className="text-blue-600 text-2xl" />
    </div>
    <div className="flex-1 min-w-0">
      <h4 className="text-base font-black text-gray-900 tracking-tight truncate group-hover:text-blue-700 transition-colors uppercase">
        {user.username || user.email.split('@')[0]}
      </h4>
      <div className="flex items-center gap-2 mt-0.5">
        <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest">{roleLabel[user.role] || `Role ${user.role}`}</p>
        <span className="w-1 h-1 rounded-full bg-gray-200" />
        <p className="text-[8px] font-bold text-blue-500/50 truncate uppercase">{user.email.split('@')[0]}</p>
      </div>
    </div>
    <div className="text-right flex-shrink-0 pr-1">
      <p className="text-2xl font-black text-gray-900 tracking-tighter group-hover:text-blue-600 transition-colors tabular-nums">{logCount}</p>
      <p className="text-[8px] text-gray-400 font-black uppercase tracking-widest">Activity</p>
    </div>
    <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-300 group-hover:bg-blue-50 group-hover:text-blue-500 transition-all">
      <MdChevronRight className="text-xl" />
    </div>
  </button>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════
const SubscriptionDetails: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { role: currentUserRole } = useUserCredentials();

  const isManagerRole =
    Number(currentUserRole) === ROLE_BUSINESS ||
    Number(currentUserRole) === ROLE_BUSINESS_ADMINISTRATOR;

  // ─── RBAC enforcement ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (currentUserRole !== undefined && currentUserRole !== null) {
      if (!isManagerRole) {
        toast.error("Access restricted: This area is reserved for Business Owners and Organization Admins.");
        navigate("/dashboard");
      }
    }
  }, [currentUserRole, isManagerRole, navigate]);

  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (activeTab === "pentest_reports" && Number(currentUserRole) === ROLE_BUSINESS_ADMINISTRATOR) {
      setActiveTab("overview");
    }
  }, [activeTab, currentUserRole]);

  const [subscription, setSubscription] = useState<any>(null);
  const [allSubscriptions, setAllSubscriptions] = useState<any[]>([]);
  const [quota, setQuota] = useState<{ quick: QuotaBlock; comprehensive: QuotaBlock } | null>(null);
  const [programs, setPrograms] = useState<CTBProgram[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [retests, setRetests] = useState<any[]>([]);
  const [pentestReports, setPentestReports] = useState<any[]>([]);
  // Activity logs state
  const [subUsers, setSubUsers] = useState<any[]>([]);
  const [allLogs, setAllLogs] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Assets tab state
  const [assetsData, setAssetsData] = useState<{ assets: any[]; targets: any[] }>({ assets: [], targets: [] });

  const [logsLoading, setLogsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);

  useEffect(() => { fetchSubscriptionDetails(); }, [subscriptionId]);
  useEffect(() => { if (subscription) fetchTabData(); }, [subscription, activeTab]);

  const fetchSubscriptionDetails = async () => {
    try {
      setLoading(true);
      // 1. Fetch all subscriptions to find the specific one
      const subRes = await axios.get("/v2/companies/my-company/subscriptions");
      const subs = safeArray(subRes.data?.data ?? subRes.data);
      setAllSubscriptions(subs);

      const found = subs.find((s: any) => s.subscription_id === parseInt(subscriptionId || "0")) || subs[0];
      
      if (!found) {
        toast.error("Subscription not found");
        navigate("/dashboard/subscription");
        return;
      }
      setSubscription(found);

      // 2. Fetch quota for THIS specific subscription's period
      const quotaRes = await axios.get("/v2/program-runs/quota", {
        params: { date: found.start_date }
      });
      setQuota(quotaRes.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  };

  const fetchTabData = async () => {
    if (!subscription) return;
    const { start_date, end_date } = subscription;

    if (activeTab === "activity_logs") {
      fetchActivityLogs();
      return;
    }

    if (activeTab === "overview") {
      // Fetch all for summary counts
      try {
        const isBusinessOwner = Number(currentUserRole) === ROLE_BUSINESS;
        const overviewRequests: Promise<any>[] = [
          axios.get("/v2/programs", { params: { startDate: start_date, endDate: end_date, limit: 100 } }),
          axios.get("/v2/reports", { params: { startDate: start_date, endDate: end_date, pageCount: 100 } }),
          axios.get("/v2/retests", { params: { startDate: start_date, endDate: end_date, limit: 100 } })
        ];
        if (isBusinessOwner) {
          overviewRequests.push(
            axios.get("/v2/automated-report/admin/pentest-reports", {
              params: { date_from: start_date, date_to: end_date }
            })
          );
        }

        const results = await Promise.all(overviewRequests);
        const [pr, rr, rtr, ptr] = isBusinessOwner
          ? results
          : [...results, { data: { data: { reports: [] } } }];

        setPrograms(safeArray(pr.data?.programs ?? pr.data?.data ?? pr.data).map((p: any) => parseProgramDetails(p)));
        setReports(safeArray(rr.data?.reports ?? rr.data?.data ?? rr.data));
        setRetests(safeArray(rtr.data?.retests ?? rtr.data?.data ?? rtr.data));
        setPentestReports(safeArray(ptr.data?.data?.reports ?? ptr.data?.data ?? ptr.data));
      } catch (err) {
        console.error("Error fetching overview summary:", err);
      }
      return;
    }

    setTabLoading(true);
    try {
      if (activeTab === "assets") {
        // Fetch programs first if they aren't loaded yet
        let currentPrograms = programs;
        if (programs.length === 0) {
          const pr = await axios.get("/v2/programs", { params: { startDate: start_date, endDate: end_date, limit: 100 } });
          const raw = pr.data?.programs ?? pr.data?.data ?? pr.data;
          currentPrograms = safeArray(raw).map((p: any) => parseProgramDetails(p));
          setPrograms(currentPrograms);
        }

        // Fetch scope for each program
        const scopePromises = currentPrograms.map(p =>
          axios.get(`/v2/programs/${p.id}/scope-with-snapshots`).catch(() => null)
        );
        const results = await Promise.all(scopePromises);

        // Aggregate and deduplicate
        const assetsMap = new Map();
        const targetsMap = new Map();

        results.forEach(res => {
          if (!res?.data?.data) return;
          const { asset, targets } = res.data.data;
          if (asset && asset.id) {
            assetsMap.set(asset.id, asset);
          }
          safeArray(targets).forEach(t => {
            const tid = t.target_id || t.id;
            if (tid) targetsMap.set(tid, t);
          });
        });

        setAssetsData({
          assets: Array.from(assetsMap.values()),
          targets: Array.from(targetsMap.values())
        });
      } else if (activeTab === "programs") {
        const r = await axios.get("/v2/programs", { params: { startDate: start_date, endDate: end_date, limit: 100 } });
        const raw = r.data?.programs ?? r.data?.data ?? r.data;
        setPrograms(safeArray(raw).map((p: any) => parseProgramDetails(p)));
      } else if (activeTab === "reports") {
        const r = await axios.get("/v2/reports", { params: { startDate: start_date, endDate: end_date, pageCount: 100 } });
        setReports(safeArray(r.data?.reports ?? r.data?.data ?? r.data));
      } else if (activeTab === "retests") {
        const r = await axios.get("/v2/retests", { params: { startDate: start_date, endDate: end_date, limit: 100 } });
        setRetests(safeArray(r.data?.retests ?? r.data?.data ?? r.data));
      } else if (activeTab === "pentest_reports") {
        if (Number(currentUserRole) !== ROLE_BUSINESS) {
          setPentestReports([]);
          return;
        }
        const r = await axios.get("/v2/automated-report/admin/pentest-reports", { params: { date_from: start_date, date_to: end_date } });
        setPentestReports(safeArray(r.data?.data?.reports ?? r.data?.data ?? r.data));
      }
    } catch { toast.error(`Failed to load ${activeTab}`); }
    finally { setTabLoading(false); }
  };

  const fetchActivityLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = {
        start_date: subscription?.start_date,
        end_date: subscription?.end_date
      };
      if (isManagerRole) {
        const [usersRes, logsRes] = await Promise.allSettled([
          axios.get("/v2/rbac/sub-users"),
          axios.get("/v2/rbac/getRbacLogs", { params })
        ]);
        if (usersRes.status === "fulfilled") setSubUsers(safeArray(usersRes.value.data));
        if (logsRes.status === "fulfilled") setAllLogs(safeArray(logsRes.value.data));
      } else {
        const r = await axios.get("/v2/rbac/my-logs", { params });
        setAllLogs(safeArray(r.data));
      }
    } catch { toast.error("Failed to load activity logs"); }
    finally { setLogsLoading(false); }
  }, [isManagerRole, subscription]);

  const selectUser = async (userId: number) => {
    setSelectedUserId(userId);
    setLogsLoading(true);
    try {
      const r = await axios.get("/v2/rbac/getRbacLogs", { 
        params: { 
          user_id: userId,
          start_date: subscription?.start_date,
          end_date: subscription?.end_date
        } 
      });
      setAllLogs(safeArray(r.data));
    } catch { toast.error("Failed to load user logs"); }
    finally { setLogsLoading(false); }
  };

  const clearUserSelection = () => {
    setSelectedUserId(null);
    fetchActivityLogs();
  };

  // ── loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-[3px] border-gray-200 border-t-blue-600" />
          <p className="text-sm text-gray-400 font-medium">Loading subscription…</p>
        </div>
      </div>
    );
  }

  if (!subscription) return null;

  const isActive = moment().isBetween(subscription.start_date, subscription.end_date);
  const isExpired = moment().isAfter(subscription.end_date);
  const daysLeft = moment(subscription.end_date).diff(moment(), "days");
  const totalDays = moment(subscription.end_date).diff(moment(subscription.start_date), "days");
  const elapsed = moment().diff(moment(subscription.start_date), "days");
  const timePct = totalDays > 0 ? Math.min((elapsed / totalDays) * 100, 100) : 100;

  const statusConfig = isActive
    ? { label: "Active", color: "bg-green-100 text-green-700", dot: "bg-green-500" }
    : isExpired
    ? { label: "Expired", color: "bg-gray-100 text-gray-500", dot: "bg-gray-400" }
    : { label: "Upcoming", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" };

  const tabs = [
    { id: "overview",        label: "Overview",        icon: <MdDashboard /> },
    { id: "assets",          label: "Assets",          icon: <MdWebAsset /> },
    { id: "programs",        label: "Programs",        icon: <MdAssignment /> },
    { id: "reports",         label: "Reports",         icon: <MdBugReport /> },
    { id: "retests",         label: "Retests",         icon: <MdTimeline /> },
    { id: "pentest_reports", label: "Pentest Reports", icon: <MdSecurity /> },
    { id: "activity_logs",   label: "Activity Logs",   icon: <MdHistory /> }
  ].filter(t => {
    // Hide pentest reports for business admin / org admin
    if (t.id === "pentest_reports" && Number(currentUserRole) === ROLE_BUSINESS_ADMINISTRATOR) return false;
    return true;
  });

  // Logs to render
  const displayedLogs = selectedUserId
    ? allLogs.filter(l => l.user_id === selectedUserId)
    : allLogs;

  const selectedUserInfo = subUsers.find(u => u.user_id === selectedUserId);

  return (
    <div className="bg-white/80 min-h-screen text-gray-900">
      {/* ── Banner ── */}
      <div className="relative overflow-hidden bg-[#010D2C] px-6 py-8">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl opacity-50" />
        
        <div className="max-w-7xl mx-auto relative">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard/subscription")}
                className="w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-xl border border-white/10 transition-all group"
              >
                <MdArrowBack className="text-lg text-white group-hover:-translate-x-1 transition-transform" />
              </button>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-black text-white tracking-tighter uppercase">
                    {subscription.subscription_name || `${subscription.subscription_type} Plan`}
                  </h1>
                  <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 backdrop-blur-md ${statusConfig.color.replace('bg-', 'bg-white/10 ')}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${statusConfig.dot}`} />
                    {statusConfig.label}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-indigo-200/40 font-bold text-[10px] uppercase tracking-widest">
                  <div className="flex items-center gap-1.5">
                    <MdCalendarToday className="text-blue-400" />
                    {moment(subscription.start_date).format("MMM D, YYYY")} – {moment(subscription.end_date).format("MMM D, YYYY")}
                  </div>
                  {isActive && (
                    <div className="flex items-center gap-1.5 text-emerald-400/80">
                      <MdAccessTime />
                      {daysLeft} DAYS LEFT
                    </div>
                  )}

                  {allSubscriptions.length > 1 && (
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black text-indigo-200/60 uppercase tracking-widest">Switch Term:</span>
                      <select
                        value={subscription.subscription_id}
                        onChange={(e) => navigate(`/dashboard/subscription/${e.target.value}`)}
                        className="bg-white/10 text-white text-[11px] font-bold px-2.5 py-1 rounded-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
                      >
                        {allSubscriptions.map((s, idx) => (
                          <option key={s.subscription_id} value={s.subscription_id} className="bg-[#010D2C] text-white">
                            {s.subscription_name || `Term #${allSubscriptions.length - idx} (${s.subscription_type})`} ({moment(s.start_date).format("MMM YYYY")} - {moment(s.end_date).format("MMM YYYY")})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {isActive && (
              <div className="md:w-48">
                <div className="flex justify-between text-[9px] font-black uppercase tracking-widest text-indigo-200/40 mb-1.5">
                  <span>Cycle</span>
                  <span>{timePct.toFixed(0)}%</span>
                </div>
                <div className="w-full bg-white/5 rounded-full h-1.5 p-0.5 border border-white/5 shadow-inner">
                  <div 
                    className="bg-blue-500 h-full rounded-full transition-all duration-1000" 
                    style={{ width: `${timePct}%` }} 
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Tabs (Compact Pill) ── */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-100 shadow-sm sticky top-0 z-30 px-6">
        <div className="max-w-7xl mx-auto py-2">
          <div className="flex bg-gray-100/60 p-1 rounded-xl w-fit max-w-full overflow-x-auto no-scrollbar">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => { setActiveTab(t.id); setSelectedUserId(null); setSearchTerm(""); }}
                className={`flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all duration-300 ${
                  activeTab === t.id
                    ? "bg-white text-blue-600 shadow-sm ring-1 ring-gray-200"
                    : "text-gray-400 hover:text-gray-900"
                }`}
              >
                <span className="text-base">{t.icon}</span>
                <span className="whitespace-nowrap">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Search Bar (Unified Search) ── */}
      {["programs", "reports", "retests", "pentest_reports", "assets", "activity_logs"].includes(activeTab) && (
        <div className="max-w-7xl mx-auto px-6 mt-8 mb-6">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <FiSearch className="h-4 w-4 text-gray-400 group-focus-within:text-blue-500 transition-colors" />
            </div>
            <input
              type="text"
              placeholder={`Search ${activeTab.replace("_", " ")}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-11 pr-4 py-3.5 bg-white border border-gray-100 rounded-2xl text-sm placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-200 transition-all shadow-sm group-hover:shadow-md"
            />
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {tabLoading && !["overview", "activity_logs"].includes(activeTab) ? (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border-2 border-blue-100" />
                  <div className="absolute top-0 left-0 w-12 h-12 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-[0.3em] text-gray-400 animate-pulse">Syncing Data...</p>
              </div>
            ) : (
              <>
                {/* ── ASSETS ── */}
                {activeTab === "assets" && (
                  <div className="space-y-6">
                    {assetsData.assets.length > 0 ? (
                      assetsData.assets.map(asset => (
                        <div key={asset.id} className="bg-white rounded-2xl border border-gray-100 shadow-md shadow-gray-200/20 overflow-hidden group">
                          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center text-blue-600 group-hover:scale-105 transition-transform duration-500">
                                <MdWebAsset className="text-xl" />
                              </div>
                              <div>
                                <h3 className="text-base font-black text-gray-900 tracking-tight uppercase">{asset.name}</h3>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">{asset.category || "General Asset"}</span>
                                  <span className="w-1 h-1 rounded-full bg-gray-200" />
                                  <span className="text-[9px] font-bold text-blue-600/50">ID: {asset.id}</span>
                                </div>
                              </div>
                            </div>
                            <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1 rounded-full border ${asset.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                              {asset.status || "Draft"}
                            </span>
                          </div>
                          <div className="p-6 bg-white/30">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {assetsData.targets
                                .filter(t => t.asset_id === asset.id)
                                .map(target => (
                                  <div key={target.target_id || target.id} className="p-4 rounded-xl border border-gray-100 bg-white hover:border-blue-100 hover:shadow-sm transition-all duration-300">
                                    <div className="flex items-center gap-3 mb-2">
                                      <div className="w-6 h-6 rounded bg-blue-50 text-blue-500 flex items-center justify-center text-xs"><MdLocationOn /></div>
                                      <span className="font-black text-gray-900 tracking-tight uppercase text-[10px]">{target.target_name}</span>
                                    </div>
                                    <div className="space-y-2 pl-9">
                                      <div className="flex flex-col gap-1">
                                        <span className="text-[7px] font-black uppercase tracking-widest text-gray-400">Endpoint / Domain</span>
                                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50/50 px-2 py-1 rounded-lg border border-blue-100 break-all leading-relaxed lowercase tabular-nums">
                                          {target.target_value || target.value}
                                        </span>
                                      </div>
                                      <div className="flex justify-between items-center text-[8px] uppercase font-black tracking-widest text-gray-400 pt-1 border-t border-gray-50">
                                        <span>Classification</span>
                                        <span className="text-gray-900 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100">{target.target_type}</span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <EmptyState label="No assets or targets found." icon={<MdWebAsset />} />
                    )}
                  </div>
                )}
                {activeTab === "overview" && (
                  <div className="space-y-8">
                    {quota ? (
                      <div>
                        <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em] mb-4 pl-1">Quota Summary</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <QuotaCard label="Quick Runs" icon={<FiZap className="text-emerald-600" />} accent="bg-emerald-50" block={quota.quick} />
                          <QuotaCard label="Comprehensive Run" icon={<FiLayers className="text-violet-600" />} accent="bg-violet-50" block={quota.comprehensive} />
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {[
                          { label: "Quick Runs Quota", value: subscription.quick_runs_quota, sub: `${subscription.quick_run_duration} days`, icon: <FiZap className="text-emerald-600" />, accent: "bg-emerald-50" },
                          { label: "Comp. Runs Quota", value: subscription.comprehensive_runs_quota, sub: `${subscription.comprehensive_run_duration} days`, icon: <FiLayers className="text-violet-600" />, accent: "bg-violet-50" }
                        ].map(c => (
                          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-md p-5 flex items-center gap-4">
                            <div className={`w-14 h-14 rounded-xl ${c.accent} flex items-center justify-center text-2xl shadow-inner`}>{c.icon}</div>
                            <div>
                              <p className="text-[9px] text-gray-400 font-black uppercase tracking-[0.2em] mb-0.5">{c.label}</p>
                              <p className="text-3xl font-black text-gray-900 tracking-tighter">{c.value}</p>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest opacity-60">{c.sub}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div>
                      <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em] mb-4 pl-1">Cycle Details</h2>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[
                          { icon: <MdShield className="text-emerald-600" />, accent: "bg-emerald-50", label: "Subscription Name", value: subscription.subscription_name || `${subscription.subscription_type} Plan` },
                          { icon: <MdCalendarToday className="text-blue-600" />, accent: "bg-blue-50", label: "Start Date", value: moment(subscription.start_date).format("MMM D, YYYY") },
                          { icon: <MdAccessTime className="text-rose-500" />, accent: "bg-rose-50", label: "End Date", value: moment(subscription.end_date).format("MMM D, YYYY") },
                          { icon: <MdTrendingUp className="text-indigo-500" />, accent: "bg-indigo-50", label: "Total Duration", value: `${totalDays} Days` }
                        ].map(c => (
                          <div key={c.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 group hover:shadow-md transition-shadow">
                            <div className={`w-10 h-10 rounded-xl ${c.accent} flex items-center justify-center flex-shrink-0 shadow-inner group-hover:scale-105 transition-transform`}>{c.icon}</div>
                            <div>
                              <p className="text-[8px] text-gray-400 font-black uppercase tracking-[0.2em] mb-0.5">{c.label}</p>
                              <p className="text-[11px] font-black text-gray-900 tracking-tight uppercase">{c.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h2 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.25em] mb-4 pl-1">Activity Summary</h2>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { label: "Programs", count: programs.length || "0", icon: <MdAssignment />, tab: "programs", color: "text-blue-600", bg: "bg-blue-50" },
                          { label: "Reports", count: reports.length || "0", icon: <MdBugReport />, tab: "reports", color: "text-rose-600", bg: "bg-rose-50" },
                          { label: "Retests", count: retests.length || "0", icon: <MdTimeline />, tab: "retests", color: "text-amber-600", bg: "bg-amber-50" },
                          { label: "Pentest Reports", count: pentestReports.length || "0", icon: <MdSecurity />, tab: "pentest_reports", color: "text-purple-600", bg: "bg-purple-50" }
                        ].filter(c => {
                          if (c.tab === "pentest_reports" && Number(currentUserRole) === ROLE_BUSINESS_ADMINISTRATOR) return false;
                          return true;
                        }).map(c => (
                          <button 
                            key={c.label} 
                            onClick={() => setActiveTab(c.tab)} 
                            className={`bg-white rounded-2xl border border-gray-100 shadow-sm p-5 text-left hover:shadow-md hover:border-transparent transition-all duration-300 group overflow-hidden relative`}
                          >
                            <div className={`absolute top-0 right-0 w-16 h-16 ${c.bg} rounded-full -mr-8 -mt-8 opacity-40 group-hover:scale-125 transition-transform duration-700`} />
                            <div className={`w-10 h-10 rounded-xl ${c.bg} ${c.color} flex items-center justify-center text-xl mb-3 relative shadow-inner`}>{c.icon}</div>
                            <p className="text-3xl font-black text-gray-900 tracking-tighter group-hover:text-blue-600 transition-colors relative tabular-nums">{c.count}</p>
                            <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5 relative">{c.label}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === "programs" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(() => {
                      const filtered = programs.filter(p => {
                        if (!searchTerm) return true;
                        const s = searchTerm.toLowerCase();
                        return p.title?.toLowerCase().includes(s) || p.description?.toLowerCase().includes(s);
                      });
                      
                      return filtered.length > 0
                        ? filtered.map(p => (
                            <motion.div key={p.id} whileHover={{ y: -3 }} transition={{ duration: 0.2 }}>
                              <ProgramCard program={p} onClick={() => navigate(`/dashboard/programs/${p.id}`)} />
                            </motion.div>
                          ))
                        : <EmptyState label="No defensive programs match your search." icon={<MdAssignment />} />;
                    })()}
                  </div>
                )}

                {/* ── REPORTS ── */}
                {activeTab === "reports" && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30">
                      <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Detailed Findings</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50/50 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-3.5">Finding Description</th>
                            <th className="px-6 py-3.5">Impact</th>
                            <th className="px-6 py-3.5">Workflow</th>
                            <th className="px-6 py-3.5">Detected</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 bg-white">
                          {(() => {
                            const filtered = reports.filter(r => {
                              if (!searchTerm) return true;
                              const s = searchTerm.toLowerCase();
                              return r.vulnerability_name?.toLowerCase().includes(s) || r.severity_category?.toLowerCase().includes(s);
                            });
                            return filtered.length > 0
                              ? filtered.map((r, idx) => (
                                  <tr 
                                  key={r.report_id} 
                                  className={`${idx % 2 === 1 ? 'bg-gray-50/20' : 'bg-white'} hover:bg-blue-50/30 cursor-pointer transition-colors group text-xs`} 
                                  onClick={() => navigate(`/dashboard/reports/${r.report_id}`)}
                                >
                                  <td className="px-6 py-3.5">
                                    <span className="font-black text-gray-900 tracking-tight group-hover:text-blue-700 transition-colors uppercase text-[11px]">{r.report_title}</span>
                                  </td>
                                  <td className="px-6 py-3.5"><SeverityBadge sev={r.severity_category} /></td>
                                  <td className="px-6 py-3.5">
                                    <span className="text-[8px] font-black uppercase tracking-widest text-gray-500 bg-gray-100/80 px-2 py-0.5 rounded-full border border-gray-200/50">{r.state || "In Triage"}</span>
                                  </td>
                                  <td className="px-6 py-3.5 font-bold text-gray-400 tabular-nums">{moment(r.submitted_date).format("MMM D, YYYY")}</td>
                                </tr>
                              ))
                            : <tr><td colSpan={4}><EmptyState label="No vulnerability data identified." icon={<MdBugReport />} /></td></tr>;
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* ── RETESTS ── */}
                {activeTab === "retests" && (
                  <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/30">
                      <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Retest History</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-gray-50/50 text-[9px] font-black text-gray-400 uppercase tracking-[0.2em] border-b border-gray-100">
                          <tr>
                            <th className="px-6 py-3.5">Validation Target</th>
                            <th className="px-6 py-3.5">Status</th>
                            <th className="px-6 py-3.5">Initiated</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 bg-white">
                          {(() => {
                            const filtered = retests.filter(rt => {
                                if (!searchTerm) return true;
                                const s = searchTerm.toLowerCase();
                                return rt.vulnerability_name?.toLowerCase().includes(s) || rt.status?.toLowerCase().includes(s);
                            });
                            return filtered.length > 0
                              ? filtered.map((rt, idx) => (
                                  <tr 
                                  key={rt.retest_id} 
                                  className={`${idx % 2 === 1 ? 'bg-gray-50/20' : 'bg-white'} hover:bg-blue-50/30 transition-colors cursor-pointer group text-xs`} 
                                  onClick={() => navigate(`/dashboard/retests/${rt.retest_id}`)}
                                >
                                  <td className="px-6 py-3.5 font-black text-gray-900 tracking-tight group-hover:text-blue-700 transition-colors uppercase text-[11px]">{rt.report_name}</td>
                                  <td className="px-6 py-3.5">
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[8px] font-black uppercase tracking-wider border border-white/50 shadow-sm ${rt.status === "Fix Verified" ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-700"}`}>
                                      <span className={`w-1 h-1 rounded-full ${rt.status === "Fix Verified" ? "bg-emerald-500" : "bg-blue-500"}`} />
                                      {rt.status}
                                    </span>
                                  </td>
                                  <td className="px-6 py-3.5 font-bold text-gray-400 tabular-nums">{moment(rt.created_at).format("MMM D, YYYY")}</td>
                                </tr>
                              ))
                            : <tr><td colSpan={3}><EmptyState label="No retest artifacts found." icon={<MdTimeline />} /></td></tr>;
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {activeTab === "pentest_reports" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {(() => {
                        const filtered = pentestReports.filter(r => {
                            if (!searchTerm) return true;
                            const s = searchTerm.toLowerCase();
                            return r.title?.toLowerCase().includes(s) || r.status?.toLowerCase().includes(s);
                        });
                        return filtered.length > 0
                          ? filtered.map(r => (
                              <motion.div 
                                key={r.id} 
                                whileHover={{ y: -5 }} 
                                transition={{ duration: 0.3, ease: "easeOut" }}
                                className="group relative"
                              >
                                <div 
                                  onClick={() => navigate(`/dashboard/pentest-reports/${r.id}/edit`)}
                                  className="bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-gray-200/40 p-6 cursor-pointer overflow-hidden relative transition-all duration-500 hover:shadow-2xl hover:shadow-purple-200/50"
                                >
                                  {/* Premium Header Accent */}
                                  <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 opacity-80" />
                                  <div className="absolute -right-12 -top-12 w-32 h-32 bg-purple-500 rounded-full blur-[64px] opacity-0 group-hover:opacity-10 transition-opacity duration-700" />

                                  <div className="flex items-center justify-between mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center text-purple-600 shadow-inner group-hover:rotate-[10deg] transition-transform duration-500">
                                      <MdSecurity className="text-2xl" />
                                    </div>
                                    <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-sm border ${
                                      r.status === 'published' 
                                        ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                                        : 'bg-amber-50 text-amber-700 border-amber-100'
                                    }`}>
                                      {r.status?.replace("_", " ")}
                                    </span>
                                  </div>

                                  <div className="mb-6">
                                    <h3 className="text-base font-black text-gray-900 tracking-tight leading-snug group-hover:text-purple-700 transition-colors line-clamp-2 uppercase">
                                      {r.title}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-2">
                                      <span className="text-[9px] font-black text-purple-600/60 uppercase tracking-[0.2em]">Verified Artifact</span>
                                      <div className="w-1 h-1 rounded-full bg-gray-200" />
                                      <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{moment(r.createdAt).format("MMM YYYY")}</span>
                                    </div>
                                  </div>

                                  <div className="pt-5 border-t border-gray-50 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center text-[10px]">
                                        <MdCheckCircle />
                                      </div>
                                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Signed & Certified</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-blue-600 group-hover:translate-x-1 transition-transform">
                                      <span className="text-[9px] font-black uppercase tracking-widest">Review Report</span>
                                      <MdArrowForward className="text-sm" />
                                    </div>
                                  </div>
                                </div>
                              </motion.div>
                            ))
                      : <EmptyState label="No certified security artifacts." icon={<MdSecurity />} />;
                    })()}
                  </div>
                )}

                {/* ── ACTIVITY LOGS ── */}
                {activeTab === "activity_logs" && (
                  <div className="space-y-6">
                    {logsLoading ? (
                      <div className="flex flex-col items-center justify-center py-20 gap-4">
                        <div className="w-12 h-12 rounded-full border-2 border-blue-100 border-t-blue-600 animate-spin" />
                        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-gray-400 animate-pulse">Syncing Log Data...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {/* Audit Log Table */}
                        <div className="md:col-span-3">
                          <div className="bg-white rounded-2xl border border-gray-100 shadow-md overflow-hidden">
                            <div className="px-8 py-5 border-b border-gray-100 bg-gray-50/30 flex items-center justify-between">
                              <div>
                                <h3 className="text-base font-black text-gray-900 tracking-tight uppercase">
                                  {selectedUserId && selectedUserInfo
                                    ? `${selectedUserInfo.username || selectedUserInfo.email.split('@')[0]}'s Audit Trail`
                                    : "System Audit Log"}
                                </h3>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest mt-0.5">Audit trail of company security actions</p>
                              </div>
                              <div className="flex items-center gap-3">
                                {isManagerRole && selectedUserId && (
                                  <button
                                    onClick={clearUserSelection}
                                    className="flex items-center gap-2 text-[8px] font-black text-blue-600 hover:text-blue-800 uppercase tracking-widest bg-blue-50 px-3 py-1.5 rounded-lg transition-colors border border-blue-100/50"
                                  >
                                    <MdArrowBack className="text-[10px]" />
                                    Show All Team Logs
                                  </button>
                                )}
                                <button onClick={fetchActivityLogs} className="flex items-center gap-1.5 text-[9px] font-black text-gray-500 uppercase tracking-widest hover:text-blue-600 transition-colors bg-gray-50 px-3 py-1.5 rounded-lg shadow-inner">
                                  <MdRefresh className={`text-sm ${logsLoading ? "animate-spin" : ""}`} />
                                  Refresh
                                </button>
                              </div>
                            </div>
                            <div className="max-h-[600px] overflow-y-auto no-scrollbar">
                              {(() => {
                                const filtered = displayedLogs.filter(l => {
                                  if (!searchTerm) return true;
                                  const s = searchTerm.toLowerCase();
                                  return (
                                    l.action?.toLowerCase().includes(s) ||
                                    l.module?.toLowerCase().includes(s) ||
                                    l.user?.username?.toLowerCase().includes(s)
                                  );
                                });
                                return filtered.length > 0
                                  ? filtered.map((log: any, idx) => <LogRow key={log.id || idx} log={log} />)
                                  : <EmptyState label="No audit activities found matching your search." icon={<MdHistory />} />;
                              })()}
                            </div>
                          </div>
                        </div>

                        {/* Team Sidebar (Manager Only) */}
                        <div className="space-y-6">
                          {isManagerRole && (
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-md p-6">
                              <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-4">Team Activity</h3>
                              <div className="space-y-3">
                                {subUsers.map(u => (
                                  <button
                                    key={u.user_id}
                                    onClick={() => selectUser(u.user_id)}
                                    className={`w-full text-left p-3 rounded-xl border transition-all duration-300 ${
                                      selectedUserId === u.user_id
                                        ? "bg-blue-50 border-blue-100 shadow-sm"
                                        : "bg-white border-transparent hover:border-gray-100 hover:bg-gray-50/50"
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${
                                        selectedUserId === u.user_id ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-400"
                                      }`}>
                                        <MdPerson />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-[10px] font-black text-gray-900 truncate uppercase tracking-tight">
                                          {u.username || u.email.split('@')[0]}
                                        </p>
                                        <p className="text-[8px] font-bold text-gray-400 uppercase opacity-60">
                                          {allLogs.filter((l: any) => l.user_id === u.user_id).length} Actions
                                        </p>
                                      </div>
                                    </div>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SubscriptionDetails;
